import React, { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Box, Group, Loader, Center } from '@mantine/core'
import {
  AmmoScoreBadge,
  AppBadge,
  AppButton,
  AppIconButton,
  AppMenu,
  AppModal,
  AppNumberInput,
  AppText,
  AppTextarea
} from '../../app/common/ui'
import { format, formatDistanceToNow } from 'date-fns'
import { toast } from 'react-toastify'
import { IoEllipsisHorizontal } from 'react-icons/io5'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import { ReloaderRecipeDto } from '../../app/models/reloaderRecipe'

export default function RecipeDetail () {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { userStore } = useStore()
  const [recipe, setRecipe] = useState<ReloaderRecipeDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [validatingForm, setValidatingForm] = useState(false)
  const [actualVelocity, setActualVelocity] = useState<number | undefined>()
  const [firearmUsed, setFirearmUsed] = useState('')
  const [validationNotes, setValidationNotes] = useState('')
  const [flagModalOpen, setFlagModalOpen] = useState(false)
  const [flagReason, setFlagReason] = useState('')

  useEffect(() => {
    const loadRecipe = async () => {
      try {
        const data = await agent.ReloaderRecipes.details(id)
        setRecipe(data)
      } catch (error) {
        toast.error('Recipe not found')
        history.push('/ammo/reloading')
      } finally {
        setLoading(false)
      }
    }
    loadRecipe()
  }, [id, history])

  const handleValidate = async () => {
    if (!recipe || !actualVelocity || !firearmUsed.trim()) {
      toast.error('Please fill in all required fields')
      return
    }
    setValidatingForm(true)
    try {
      await agent.ReloaderRecipes.validate(recipe.id, {
        actualVelocityFps: actualVelocity,
        firearmUsed: firearmUsed.trim(),
        notes: validationNotes.trim() || undefined
      })
      toast.success('Validation added!')
      setActualVelocity(undefined)
      setFirearmUsed('')
      setValidationNotes('')
      // Reload recipe
      const updated = await agent.ReloaderRecipes.details(id)
      setRecipe(updated)
    } catch (error) {
      toast.error('Failed to add validation')
    } finally {
      setValidatingForm(false)
    }
  }

  const handleRemoveValidation = async (validationId: string) => {
    if (!recipe) return
    try {
      await agent.ReloaderRecipes.unvalidate(recipe.id)
      toast.success('Validation removed')
      const updated = await agent.ReloaderRecipes.details(id)
      setRecipe(updated)
    } catch (error) {
      toast.error('Failed to remove validation')
    }
  }

  const handleFlag = async () => {
    if (!recipe || !flagReason.trim()) {
      toast.error('Please provide a reason')
      return
    }
    try {
      await agent.ReloaderRecipes.flag(recipe.id, flagReason)
      toast.success('Recipe flagged')
      setFlagModalOpen(false)
      const updated = await agent.ReloaderRecipes.details(id)
      setRecipe(updated)
    } catch (error) {
      toast.error('Failed to flag recipe')
    }
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    )
  }

  if (!recipe) {
    return <AppText>Recipe not found</AppText>
  }

  return (
    <Box sx={{ fontFamily: 'DM Sans, sans-serif', maxWidth: 760, margin: '0 auto', padding: 'var(--gs-lg)' }}>
      {/* Back Link */}
      <AppButton
        variant="subtle"
        onClick={() => history.push('/ammo/reloading')}
        sx={{ paddingLeft: 0, marginBottom: 'var(--gs-md)' }}
      >
        ← Back to Reloader Forum
      </AppButton>

      {/* Safety Banner */}
      {recipe.isFlagged && (
        <Box sx={{ background: 'var(--gs-danger)', padding: 'var(--gs-md)', borderRadius: 6, marginBottom: 'var(--gs-md)' }}>
          <AppText weight={600} sx={{ color: '#fff', marginBottom: 'var(--gs-xs)' }}>
            ⚠️ Safety Warning
          </AppText>
          <AppText size="sm" sx={{ color: '#fff' }}>
            This recipe has been flagged for safety review. Reason: {recipe.flagReason}. Use extreme caution.
          </AppText>
        </Box>
      )}

      {/* Header */}
      <Box mb="lg">
        <AppText component={Link} to={`/ammo/specs/${recipe.cartridgeSpecId}`} sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 600, color: 'var(--gs-text)', textDecoration: 'none' }}>
          {recipe.cartridgeSpecName}
        </AppText>
      </Box>

      {/* Spec Table */}
      <Box sx={{ border: '1px solid var(--gs-border)', borderRadius: 6, overflow: 'hidden', marginBottom: 'var(--gs-lg)' }}>
        <SpecRow label="Powder" value={`${recipe.powderBrand} ${recipe.powderType}`} />
        <SpecRow label="Charge Weight" value={`${recipe.chargeWeightGrMin}-${recipe.chargeWeightGrMax}gr`} />
        <SpecRow label="Primer" value={`${recipe.primerBrand} ${recipe.primerType}`} />
        {recipe.brassBrand && <SpecRow label="Brass" value={recipe.brassBrand} />}
        <SpecRow label="Bullet" value={`${recipe.bulletWeightGr}gr ${recipe.bulletType} — ${recipe.bulletBrand}`} />
        <SpecRow label="OAL" value={`${recipe.oalIn}"`} />
        {recipe.coalIn && <SpecRow label="COAL" value={`${recipe.coalIn}"`} />}
        {recipe.seatingDepthIn && <SpecRow label="Seating Depth" value={`${recipe.seatingDepthIn}"`} />}
        {recipe.estimatedVelocityFps && <SpecRow label="Est. Velocity" value={`${recipe.estimatedVelocityFps} fps`} />}
        <SpecRow label="Intended Use" value={recipe.intendedUse} />
      </Box>

      {/* Chrono Data */}
      {recipe.chronoData && (
        <Box mb="lg">
          <AppText weight={600} size="sm" mb="var(--gs-sm)">Chrono Data</AppText>
          <Box sx={{ background: 'var(--gs-surface)', padding: 'var(--gs-sm)', borderRadius: 4, fontFamily: 'JetBrains Mono, monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
            {recipe.chronoData}
          </Box>
        </Box>
      )}

      {/* Notes */}
      {recipe.notes && (
        <Box mb="lg">
          <AppText weight={600} size="sm" mb="var(--gs-sm)">Notes</AppText>
          <AppText size="sm" sx={{ lineHeight: 1.6 }}>{recipe.notes}</AppText>
        </Box>
      )}

      {/* Validations Section */}
      <Box mb="lg">
        <Group mb="md">
          <AppText weight={700} size="lg">Community Results</AppText>
          <AppBadge color="green" size="lg">{recipe.validationCount} Validated</AppBadge>
        </Group>

        {/* Validate Form */}
        {userStore.isLoggedIn && !recipe.isValidatedByCurrentUser && (
          <Box sx={{ background: 'var(--gs-surface)', padding: 'var(--gs-md)', borderRadius: 6, marginBottom: 'var(--gs-md)' }}>
            <AppText weight={600} size="sm" mb="sm">I ran this recipe</AppText>
            <AppNumberInput
              label="Actual Velocity (fps)"
              placeholder="1234"
              value={actualVelocity}
              onChange={setActualVelocity}
              mb="sm"
            />
            <AppNumberInput
              label="Firearm Used"
              placeholder="e.g., 16-inch AR-15 5.56"
              value={firearmUsed}
              onChange={(v) => setFirearmUsed(String(v || ''))}
              mb="sm"
            />
            <AppTextarea
              label="Notes (optional)"
              placeholder="Any issues or observations?"
              value={validationNotes}
              onChange={(e) => setValidationNotes(e.currentTarget.value)}
              mb="sm"
            />
            <AppButton size="sm" onClick={handleValidate} loading={validatingForm}>
              Submit Result
            </AppButton>
          </Box>
        )}

        {/* Validation Cards */}
        {recipe.validations && recipe.validations.length > 0 && (
          <Box>
            {recipe.validations.map((validation) => (
              <Box
                key={validation.id}
                sx={{
                  padding: 'var(--gs-sm)',
                  border: '1px solid var(--gs-border)',
                  borderRadius: 4,
                  marginBottom: 8
                }}
              >
                <Group position="apart" mb="xs">
                  <Group spacing="xs">
                    <AppText component={Link} to={`/profiles/${validation.validatorUsername}`} weight={500} size="sm" sx={{ color: 'var(--gs-accent)' }}>
                      {validation.validatorDisplayName || validation.validatorUsername}
                    </AppText>
                    <AmmoScoreBadge tier="Newcomer" score={0} size="xs" />
                  </Group>
                  {recipe.isValidatedByCurrentUser && validation.id === recipe.currentUserValidationId && (
                    <AppButton size="xs" variant="light" color="red" onClick={() => handleRemoveValidation(validation.id)}>
                      Remove result
                    </AppButton>
                  )}
                </Group>
                <AppText size="sm" weight={600} sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  Got {validation.actualVelocityFps} fps with {validation.firearmUsed}
                </AppText>
                {validation.notes && (
                  <AppText size="sm" color="dimmed" mt="var(--gs-xs)">{validation.notes}</AppText>
                )}
                <AppText size="xs" color="dimmed" mt="var(--gs-xs)">
                  {formatDistanceToNow(new Date(validation.createdAt), { addSuffix: true })}
                </AppText>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Author & Actions */}
      <Box sx={{ padding: 'var(--gs-md)', background: 'var(--gs-surface)', borderRadius: 6, marginTop: 'var(--gs-lg)' }}>
        <Group position="apart" align="center">
          <Group spacing="xs">
            <AppText component={Link} to={`/profiles/${recipe.authorUsername}`} weight={600} sx={{ color: 'var(--gs-accent)' }}>
              {recipe.authorDisplayName || recipe.authorUsername}
            </AppText>
            <AmmoScoreBadge tier={recipe.authorAmmoTier} score={recipe.authorAmmoScore} size="sm" />
            <AppText size="xs" color="dimmed">
              {format(new Date(recipe.createdAt), 'MMM d, yyyy')}
            </AppText>
          </Group>

          {(userStore.user?.isModerator || userStore.user?.isAdmin) && (
            <AppMenu position="bottom-end">
              <AppMenu.Target>
                <AppIconButton variant="subtle" size="sm">
                  <IoEllipsisHorizontal size={16} />
                </AppIconButton>
              </AppMenu.Target>
              <AppMenu.Dropdown>
                {userStore.user?.isModerator && !recipe.isFlagged && (
                  <AppMenu.Item onClick={() => setFlagModalOpen(true)}>Flag for Review</AppMenu.Item>
                )}
                {userStore.user?.isAdmin && (
                  <AppMenu.Item color="red">Hide Recipe</AppMenu.Item>
                )}
              </AppMenu.Dropdown>
            </AppMenu>
          )}
        </Group>
      </Box>

      {/* Flag Modal */}
      <AppModal opened={flagModalOpen} onClose={() => setFlagModalOpen(false)} title="Flag Recipe for Review">
        <AppTextarea
          label="Reason"
          placeholder="Why is this recipe unsafe?"
          value={flagReason}
          onChange={(e) => setFlagReason(e.currentTarget.value)}
          mb="md"
        />
        <Group position="right">
          <AppButton variant="light" onClick={() => setFlagModalOpen(false)}>Cancel</AppButton>
          <AppButton color="red" onClick={handleFlag}>Flag</AppButton>
        </Group>
      </AppModal>
    </Box>
  )
}

function SpecRow ({ label, value }: { label: string; value: string }) {
  return (
    <Group
      spacing="lg"
      noWrap
      sx={{
        padding: '12px',
        borderBottom: '1px solid var(--gs-border)',
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      <AppText weight={600} sx={{ background: 'var(--gs-surface)', padding: '12px', minWidth: 140 }}>
        {label}
      </AppText>
      <AppText sx={{ fontFamily: 'JetBrains Mono, monospace', flex: 1 }}>{value}</AppText>
    </Group>
  )
}
